import React, { Children, lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ShieldCheckIcon, TruckIcon, RefreshCwIcon, SparklesIcon, ShirtIcon } from 'lucide-react';
import { useStore } from '../context/StoreContext';
import { ProductCard } from '../components/ProductCard';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function HomePage() {
  const {
    products
  } = useStore();
  const featured = products.filter(p => p.isFeatured).slice(0, 8);
  const newArrivals = products.filter(p => p.isNewArrival).slice(0, 4);
  const categories = [{
    name: 'Shirts',
    image: 'https://picsum.photos/seed/catshirt/300/400',
    count: products.filter(p => p.category === 'Shirts').length
  }, {
    name: 'T-Shirts',
    image: 'https://picsum.photos/seed/cattshirt/300/400',
    count: products.filter(p => p.category === 'T-Shirts').length
  }, {
    name: 'Jeans',
    image: 'https://picsum.photos/seed/catjeans/300/400',
    count: products.filter(p => p.category === 'Jeans').length
  }, {
    name: 'Jackets',
    image: 'https://picsum.photos/seed/catjacket/300/400',
    count: products.filter(p => p.category === 'Jackets').length
  }, {
    name: 'Hoodies',
    image: 'https://picsum.photos/seed/cathoodie/300/400',
    count: products.filter(p => p.category === 'Hoodies').length
  }];
  return <div className="w-full" data-id="element-994">
      {/* Hero Banner */}
      <section className="relative overflow-hidden bg-gradient-to-br from-sky-600 via-sky-500 to-blue-700 text-white" data-id="element-995">
        <div className="absolute inset-0 opacity-10" data-id="element-996">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl" data-id="element-997" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-300 rounded-full blur-3xl" data-id="element-998" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-sky-200 rounded-full blur-3xl" data-id="element-999" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 relative z-10" data-id="element-1000">
          <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16" data-id="element-1001">
            <motion.div className="flex-1 text-center md:text-left" initial="hidden" animate="visible" variants={stagger} data-id="element-1002">
              <motion.div variants={fadeUp} transition={{
              duration: 0.5
            }} data-id="element-1003">
                <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white text-sm font-medium px-4 py-1.5 rounded-full mb-6" data-id="element-1004">
                  <SparklesIcon className="w-4 h-4" data-id="element-1005" />
                  New Collection 2026
                </span>
              </motion.div>
              <motion.h1 variants={fadeUp} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6" data-id="element-1006">
                Elevate Your
                <br data-id="element-1007" />
                <span className="text-sky-200" data-id="element-1008">Style</span>
              </motion.h1>
              <motion.p variants={fadeUp} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="text-lg text-sky-100 max-w-lg mb-8 mx-auto md:mx-0" data-id="element-1009">
                Discover premium clothing crafted for the modern individual.
                Quality fabrics, timeless designs, unbeatable prices.
              </motion.p>
              <motion.div variants={fadeUp} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start" data-id="element-1010">
                <Link to="/products" className="inline-flex items-center justify-center gap-2 bg-white text-sky-700 font-semibold px-8 py-3.5 rounded-lg hover:bg-sky-50 transition-colors shadow-lg shadow-sky-900/20" data-id="element-1011">
                  Shop Now <ArrowRightIcon className="w-4 h-4" data-id="element-1012" />
                </Link>
                <Link to="/products?sort=newest" className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white font-semibold px-8 py-3.5 rounded-lg border border-white/20 hover:bg-white/20 transition-colors" data-id="element-1013">
                  New Arrivals
                </Link>
              </motion.div>
            </motion.div>
            <motion.div className="flex-shrink-0" initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} data-id="element-1014">
              <div className="relative" data-id="element-1015">
                <div className="absolute inset-0 bg-white/20 rounded-full blur-2xl scale-110" data-id="element-1016" />
                <img src="https://cdn.magicpatterns.com/uploads/njJUJ1uVWXUUEU8dnak6Jg/Screenshot_2025-12-23-16-07-24-10_99c04817c0de5652397fc8b56c3b3817.png" alt="Sky Brand" className="relative w-48 h-48 md:w-64 md:h-64 object-contain drop-shadow-2xl" data-id="element-1017" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Brand Intro */}
      <section className="py-16 md:py-20 bg-white" data-id="element-1018">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1019">
          <motion.div className="max-w-3xl mx-auto text-center" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={stagger} data-id="element-1020">
            <motion.div variants={fadeUp} transition={{
            duration: 0.5
          }} data-id="element-1021">
              <ShirtIcon className="w-10 h-10 text-sky-500 mx-auto mb-4" data-id="element-1022" />
            </motion.div>
            <motion.h2 variants={fadeUp} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1023">
              Welcome to Sky Brand
            </motion.h2>
            <motion.p variants={fadeUp} transition={{
            duration: 0.5
          }} className="text-lg text-slate-600 leading-relaxed" data-id="element-1024">
              We believe great style shouldn't come at a premium. Sky Brand
              brings you carefully curated clothing that combines contemporary
              design with exceptional comfort. Every piece is crafted with
              attention to detail, using quality fabrics that look and feel
              amazing.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 md:py-20 bg-slate-50" data-id="element-1025">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1026">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={stagger} data-id="element-1027">
            <motion.h2 variants={fadeUp} className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10" data-id="element-1028">
              Shop by Category
            </motion.h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6" data-id="element-1029">
              {categories.map((cat, i) => <motion.div key={cat.name} variants={fadeUp} transition={{
              delay: i * 0.05
            }} data-id="element-1030">
                  <Link to={`/products?category=${cat.name}`} className="group block relative rounded-xl overflow-hidden aspect-[3/4] bg-slate-200" data-id="element-1031">
                    <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" data-id="element-1032" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" data-id="element-1033" />
                    <div className="absolute bottom-0 inset-x-0 p-4" data-id="element-1034">
                      <h3 className="text-white font-semibold text-lg" data-id="element-1035">
                        {cat.name}
                      </h3>
                      <p className="text-white/70 text-sm" data-id="element-1036">
                        {cat.count} Products
                      </p>
                    </div>
                  </Link>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 md:py-20 bg-white" data-id="element-1037">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1038">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={stagger} data-id="element-1039">
            <div className="flex items-center justify-between mb-10" data-id="element-1040">
              <motion.h2 variants={fadeUp} className="text-2xl md:text-3xl font-bold text-slate-900" data-id="element-1041">
                Featured Products
              </motion.h2>
              <motion.div variants={fadeUp} data-id="element-1042">
                <Link to="/products" className="text-sky-600 font-medium text-sm hover:text-sky-700 flex items-center gap-1" data-id="element-1043">
                  View All <ArrowRightIcon className="w-4 h-4" data-id="element-1044" />
                </Link>
              </motion.div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-id="element-1045">
              {featured.map((product, i) => <motion.div key={product.id} variants={fadeUp} transition={{
              delay: i * 0.05
            }} data-id="element-1046">
                  <ProductCard product={product} data-id="element-1047" />
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* New Arrivals */}
      {newArrivals.length > 0 && <section className="py-16 md:py-20 bg-slate-50" data-id="element-1048">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1049">
            <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={stagger} data-id="element-1050">
              <div className="flex items-center justify-between mb-10" data-id="element-1051">
                <motion.h2 variants={fadeUp} className="text-2xl md:text-3xl font-bold text-slate-900" data-id="element-1052">
                  🆕 New Arrivals
                </motion.h2>
                <motion.div variants={fadeUp} data-id="element-1053">
                  <Link to="/products?sort=newest" className="text-sky-600 font-medium text-sm hover:text-sky-700 flex items-center gap-1" data-id="element-1054">
                    View All <ArrowRightIcon className="w-4 h-4" data-id="element-1055" />
                  </Link>
                </motion.div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-id="element-1056">
                {newArrivals.map((product, i) => <motion.div key={product.id} variants={fadeUp} transition={{
              delay: i * 0.05
            }} data-id="element-1057">
                    <ProductCard product={product} data-id="element-1058" />
                  </motion.div>)}
              </div>
            </motion.div>
          </div>
        </section>}

      {/* Why Choose Us */}
      <section className="py-16 md:py-20 bg-white" data-id="element-1059">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1060">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={stagger} data-id="element-1061">
            <motion.h2 variants={fadeUp} className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-12" data-id="element-1062">
              Why Choose Sky Brand?
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1063">
              <motion.div variants={fadeUp} className="text-center p-6 rounded-2xl bg-sky-50 border border-sky-100" data-id="element-1064">
                <div className="w-14 h-14 bg-sky-100 rounded-xl flex items-center justify-center mx-auto mb-4" data-id="element-1065">
                  <ShieldCheckIcon className="w-7 h-7 text-sky-600" data-id="element-1066" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-1067">
                  Premium Quality
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-1068">
                  Every garment is crafted from carefully selected fabrics,
                  ensuring durability, comfort, and a premium feel that lasts.
                </p>
              </motion.div>

              <motion.div variants={fadeUp} className="text-center p-6 rounded-2xl bg-sky-50 border border-sky-100" data-id="element-1069">
                <div className="w-14 h-14 bg-sky-100 rounded-xl flex items-center justify-center mx-auto mb-4" data-id="element-1070">
                  <TruckIcon className="w-7 h-7 text-sky-600" data-id="element-1071" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-1072">
                  Fast Delivery
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-1073">
                  Get your orders delivered quickly and reliably. Free shipping
                  on orders above ₹999 across India.
                </p>
              </motion.div>

              <motion.div variants={fadeUp} className="text-center p-6 rounded-2xl bg-sky-50 border border-sky-100" data-id="element-1074">
                <div className="w-14 h-14 bg-sky-100 rounded-xl flex items-center justify-center mx-auto mb-4" data-id="element-1075">
                  <RefreshCwIcon className="w-7 h-7 text-sky-600" data-id="element-1076" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-1077">
                  Easy Returns
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-1078">
                  Not satisfied? Return within 7 days for a full refund. No
                  questions asked, hassle-free process.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-slate-900 to-slate-800 text-white" data-id="element-1079">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1080">
          <motion.div className="max-w-2xl mx-auto text-center" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={stagger} data-id="element-1081">
            <motion.h2 variants={fadeUp} className="text-2xl md:text-3xl font-bold mb-3" data-id="element-1082">
              Stay in the Loop
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 mb-8" data-id="element-1083">
              Subscribe to get exclusive offers, new arrival alerts, and style
              tips.
            </motion.p>
            <motion.form variants={fadeUp} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" onSubmit={e => {
            e.preventDefault();
            alert('Thanks for subscribing!');
          }} data-id="element-1084">
              <input type="email" placeholder="Enter your email" required className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent" data-id="element-1085" />
              <button type="submit" className="px-6 py-3 bg-sky-500 text-white font-semibold rounded-lg hover:bg-sky-400 transition-colors whitespace-nowrap" data-id="element-1086">
                Subscribe
              </button>
            </motion.form>
          </motion.div>
        </div>
      </section>
    </div>;
}